import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { applicationsV2, type ApplicationActivityItem } from "@/features/applications/api/applications.api";
import { APPLICATION_STATUS_LABELS } from "@/components/StatusPill";
import { Notice } from "@/components/Notice";
import { Loading } from "@/components/Loading";
import { formatDateTime } from "@/utils/dates";
import { SectionCard } from "../primitives";

const label = (status: string | null) => (status ? APPLICATION_STATUS_LABELS[status] ?? status : "—");

export function ActivityPanel({ id, reloadKey }: { id: string; reloadKey: string }) {
  const [items, setItems] = useState<ApplicationActivityItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    applicationsV2.activity(id)
      .then((r) => { if (!cancelled) setItems(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Couldn't load the activity."); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!items) return <Loading>Loading activity…</Loading>;

  return (
    <SectionCard title="Activity">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No status or pricing changes yet.</p>
      ) : (
        <ol className="divide-y divide-slate-100">
          {items.map((i, n) => (
            <li key={n} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
              <span className="text-slate-800">
                {i.kind === "status" ? (
                  <>
                    <span className="font-medium">Status</span>{" "}
                    {i.fromStatus ? <>{label(i.fromStatus)} → </> : "set to "}
                    <span className="font-medium">{label(i.toStatus)}</span>
                  </>
                ) : i.kind === "note" ? (
                  <>
                    <span className="font-medium">Note</span>{" "}
                    <span>{i.newValue}</span>
                  </>
                ) : i.kind === "merge" ? (
                  <>
                    <span className="font-medium">{i.field === "out" ? "Un-merged" : "Merged in"}</span>{" "}
                    <Link to={`/applications/v2/${i.newValue}`} className="text-brand-600 hover:underline">
                      {i.field === "out" ? "the other application" : "another application"}
                    </Link>
                  </>
                ) : i.kind === "owner" ? (
                  <>
                    <span className="font-medium">Owner</span>{" "}
                    <span className="text-slate-500">{i.oldValue ?? "—"}</span> → <span className="font-medium">{i.newValue ?? "—"}</span>
                  </>
                ) : i.field ? (
                  <>
                    <span className="font-medium">Pricing · {i.field}</span>{" "}
                    <span className="text-slate-500">{i.oldValue ?? "—"}</span> → <span className="font-medium">{i.newValue ?? "—"}</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">Pricing</span>{" "}
                    {i.oldValue && <><span className="text-slate-500">{i.oldValue}</span> → </>}
                    <span className="font-medium">{i.newValue ?? "cleared"}</span>
                  </>
                )}
              </span>
              <span className="whitespace-nowrap text-xs text-slate-500">{i.by} · {formatDateTime(i.at)}</span>
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}
